import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tag, Plus, Trash2, RefreshCw, Percent, User, Edit2, Check, X } from 'lucide-react';
import api from '../../services/api';
import { formatDate } from '../../utils/formatDate';
import { toast } from 'sonner';

const DiscountManagement = () => {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ user_email: '', discount_percentage: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const fetchDiscounts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/discounts');
      setDiscounts(res.data.data || []);
    } catch {
      toast.error('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDiscounts(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const pct = parseFloat(form.discount_percentage);
    if (!form.user_email) return toast.error('Email is required');
    if (!pct || pct <= 0 || pct > 100) return toast.error('Enter a valid percentage (1–100)');
    try {
      setSubmitting(true);
      await api.post('/admin/discounts', {
        user_email: form.user_email.trim(),
        discount_percentage: pct,
        note: form.note.trim() || null
      });
      toast.success('Discount saved');
      setForm({ user_email: '', discount_percentage: '', note: '' });
      fetchDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save discount');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (email) => {
    if (!window.confirm(`Remove discount for ${email}?`)) return;
    try {
      await api.delete(`/admin/discounts/${encodeURIComponent(email)}`);
      toast.success('Discount removed');
      fetchDiscounts();
    } catch {
      toast.error('Failed to remove discount');
    }
  };

  const handleEditSave = async (email) => {
    const pct = parseFloat(editValue);
    if (!pct || pct <= 0 || pct > 100) return toast.error('Enter a valid percentage (1–100)');
    try {
      await api.post('/admin/discounts', { user_email: email, discount_percentage: pct });
      toast.success('Discount updated');
      setEditingId(null);
      fetchDiscounts();
    } catch {
      toast.error('Failed to update discount');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="h-8 w-8 text-orange-600" />
            Discount Management
          </h1>
          <p className="text-gray-600 mt-1">Assign custom discounts to users by email</p>
        </div>
        <Button variant="outline" onClick={fetchDiscounts} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Add / Update Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-orange-500" /> Add / Update Discount
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="User email address"
                value={form.user_email}
                onChange={e => setForm(f => ({ ...f, user_email: e.target.value }))}
                className="pl-10"
                type="email"
                required
              />
            </div>
            <div className="w-40 relative">
              <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Discount %"
                value={form.discount_percentage}
                onChange={e => setForm(f => ({ ...f, discount_percentage: e.target.value }))}
                className="pl-10"
                type="number"
                min="1"
                max="100"
                step="0.1"
                required
              />
            </div>
            <div className="flex-1">
              <Input
                placeholder="Note (optional)"
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
            >
              {submitting ? 'Saving...' : 'Save Discount'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Active Discounts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Discounts ({discounts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-100 rounded" />)}
            </div>
          ) : discounts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Tag className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              No active discounts yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {discounts.map(d => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{d.user_name || '—'}</div>
                            <div className="text-xs text-gray-500">{d.user_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {editingId === d.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="1" max="100" step="0.1"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-sm text-gray-500">%</span>
                            <Button size="sm" variant="ghost" onClick={() => handleEditSave(d.user_email)}>
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              <X className="h-4 w-4 text-gray-400" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-800 text-sm font-bold">
                              {d.discount_percentage}% OFF
                            </Badge>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setEditingId(d.id); setEditValue(d.discount_percentage); }}
                            >
                              <Edit2 className="h-3 w-3 text-gray-400" />
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{d.note || '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {formatDate(d.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRemove(d.user_email)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DiscountManagement;
